import { isImageGraphic } from '../../symbols/model';
import { pointPresentation, markerPresentation } from '../../symbols/scene';
import { SymbolCanvasPainter } from '../../symbols/canvas';
import type { PreparedSymbolCatalogue } from '../../symbols/images';
import { loadLegendMeasurement } from '../legendMeasurement';
import {
  legendAreaOutline,
  legendLineDashes,
  markerLegendLongEdge,
  type LegendSample,
} from '../legendSamples';
import type { GeometryFamily } from '../../styles/geometry';
import type { PointSymbol, VectorSymbols } from '../../styles/model';
import type { AnnotationLegendSymbol } from '../../annotations/legend';
import type { CompositionTypography } from '../model';
import { northArrowPrimitives } from '../northArrow';
import {
  type LegendPhysicalPlan,
  type LegendTextMeasurement,
  wrapLegendText,
} from '../legendLayout';
import {
  compositionCanvasFont,
  compositionFontLoadRequest,
  compositionTextBaseline,
} from '../typography';
import {
  staticLegendLayout,
  type StaticCompositionRenderPlan,
  type StaticTextPlan,
} from './planning';
import { StaticExportError } from './model';

const FONT_LOAD_TIMEOUT_MS = 5_000;
function typographyFont(typography: CompositionTypography, dpi: number): string {
  return compositionCanvasFont(typography, dpi);
}

export async function ensureCompositionFontsLoaded(
  plan: StaticCompositionRenderPlan,
  timeoutMs = FONT_LOAD_TIMEOUT_MS,
): Promise<void> {
  if (!document.fonts?.load) return;
  const typography = [
    plan.title.typography,
    plan.subtitle.typography,
    plan.sourcesAttribution.typography,
    plan.legend.bodyTypography,
    plan.legend.heading.typography,
  ];
  const requests = [...new Set(typography.map(compositionFontLoadRequest))];
  requests.push('normal 400 48px "PlaceioMaps-Inter"', 'normal 700 48px "PlaceioMaps-Inter"');
  let timeout: number | undefined;
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeout = window.setTimeout(
        () => reject(new StaticExportError('fonts', 'Could not load the fonts for export.')),
        timeoutMs,
      );
    });
    const loaded = await Promise.race([
      Promise.all(requests.map((request) => document.fonts.load(request))),
      timeoutPromise,
    ]);
    if (loaded.some((faces) => faces.length === 0)) {
      throw new StaticExportError('fonts', 'Could not load the fonts for export.');
    }
  } finally {
    if (timeout !== undefined) window.clearTimeout(timeout);
  }
}

function textX(plan: StaticTextPlan): number {
  if (plan.typography.alignment === 'centre') return plan.position.x + plan.widthPx / 2;
  if (plan.typography.alignment === 'right') return plan.position.x + plan.widthPx;
  return plan.position.x;
}

function canvasAlignment(alignment: CompositionTypography['alignment']): CanvasTextAlign {
  return alignment === 'centre' ? 'center' : alignment;
}

function drawText(
  ctx: CanvasRenderingContext2D,
  plan: StaticTextPlan,
  dpi: number,
  wrap = false,
): void {
  if (!plan.enabled || !plan.text) return;
  ctx.save();
  ctx.fillStyle = plan.typography.colour;
  ctx.font = typographyFont(plan.typography, dpi);
  ctx.textAlign = canvasAlignment(plan.typography.alignment);
  ctx.textBaseline = 'alphabetic';
  const metrics = ctx.measureText(plan.text);
  const baseline = compositionTextBaseline(
    plan.position.y,
    plan.fontSizePx,
    metrics.fontBoundingBoxAscent ?? plan.fontSizePx * 0.8,
    metrics.fontBoundingBoxDescent ?? plan.fontSizePx * 0.2,
  );
  if (wrap) {
    const lines = wrapLegendText(
      plan.text,
      plan.widthPx,
      (candidate) => ctx.measureText(candidate).width,
    );
    lines.forEach((line, index) =>
      ctx.fillText(line, textX(plan), baseline + index * plan.fontSizePx * 1.15),
    );
  } else ctx.fillText(plan.text, textX(plan), baseline, plan.widthPx);
  ctx.restore();
}

function drawPoint(
  ctx: CanvasRenderingContext2D,
  symbol: PointSymbol,
  x: number,
  y: number,
  size: number,
  outline: number,
): void {
  ctx.beginPath();
  if (symbol.shape === 'square') ctx.rect(x - size, y - size, size * 2, size * 2);
  else if (symbol.shape === 'triangle') {
    ctx.moveTo(x, y - size * 1.15);
    ctx.lineTo(x - size, y + size * 0.85);
    ctx.lineTo(x + size, y + size * 0.85);
    ctx.closePath();
  } else ctx.arc(x, y, size, 0, Math.PI * 2);
  ctx.globalAlpha = symbol.opacity;
  ctx.fillStyle = symbol.fillColor;
  ctx.fill();
  if (symbol.outlineWidth > 0) {
    ctx.strokeStyle = symbol.outlineColor;
    ctx.lineWidth = outline;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawLegendSymbol(
  ctx: CanvasRenderingContext2D,
  geometry: GeometryFamily | 'mixed',
  symbols: VectorSymbols,
  x: number,
  y: number,
  width: number,
  height: number,
  sample: LegendSample,
  scale: number,
  artwork: SymbolCanvasPainter,
): void {
  ctx.save();
  const family = geometry === 'mixed' ? 'polygon' : geometry;
  if (family === 'point') {
    const previewSize = sample.pointRadius;
    if (
      artwork.draw(
        ctx,
        symbols.point.shape,
        symbols.point.fillColor,
        symbols.point.tint,
        x + width / 2,
        y + height / 2,
        previewSize * scale * 2,
        symbols.point.rotationDegrees ?? 0,
        symbols.point.opacity,
        pointPresentation(symbols.point),
      )
    ) {
      ctx.restore();
      return;
    }
    drawPoint(
      ctx,
      symbols.point,
      x + width / 2,
      y + height / 2,
      previewSize * scale,
      sample.pointOutline * scale,
    );
  } else if (family === 'line') {
    if (sample.lineWidth <= 0) {
      ctx.restore();
      return;
    }
    ctx.save();
    ctx.globalAlpha = symbols.line.opacity;
    ctx.strokeStyle = symbols.line.color;
    ctx.lineWidth = sample.lineWidth * scale;
    ctx.lineCap = 'round';
    ctx.setLineDash(
      legendLineDashes(symbols.line.dash, sample.lineWidth).map((value) => value * scale),
    );
    ctx.beginPath();
    ctx.moveTo(x + scale, y + height / 2);
    ctx.lineTo(x + width - scale, y + height / 2);
    ctx.stroke();
    ctx.restore();
  } else {
    ctx.save();
    ctx.globalAlpha = symbols.polygon.fillOpacity;
    ctx.fillStyle = symbols.polygon.fillColor;
    ctx.fillRect(x + scale, y + height * 0.15, width - 2 * scale, height * 0.7);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = symbols.polygon.outlineColor;
    ctx.lineWidth = legendAreaOutline(symbols.polygon.outlineWidth) * (height / 18);
    if (symbols.polygon.outlineWidth > 0)
      ctx.strokeRect(x + scale, y + height * 0.15, width - 2 * scale, height * 0.7);
    ctx.restore();
  }
  ctx.restore();
}

function drawAnnotationLegendSymbol(
  ctx: CanvasRenderingContext2D,
  symbol: AnnotationLegendSymbol,
  x: number,
  y: number,
  width: number,
  height: number,
  artwork: SymbolCanvasPainter,
): void {
  ctx.save();
  const scale =
    height /
    (symbol.kind === 'marker' && isImageGraphic(symbol.shape) && symbol.backing?.enabled ? 30 : 18);
  if (symbol.kind === 'marker') {
    if (
      artwork.draw(
        ctx,
        symbol.shape,
        symbol.fillColour,
        symbol.tint,
        x + width / 2,
        y + height / 2,
        markerLegendLongEdge(symbol) * scale,
        symbol.rotationDegrees ?? 0,
        symbol.opacity,
        markerPresentation(symbol),
      )
    ) {
      ctx.restore();
      return;
    }
    const size = Math.min(6.5, Math.max(3, 2.3 + symbol.size * 0.24)) * scale;
    const centreX = x + width / 2;
    const centreY = y + height / 2;
    ctx.beginPath();
    if (symbol.shape === 'circle') ctx.arc(centreX, centreY, size, 0, Math.PI * 2);
    else if (symbol.shape === 'diamond') {
      ctx.moveTo(centreX, centreY - size);
      ctx.lineTo(centreX + size, centreY);
      ctx.lineTo(centreX, centreY + size);
      ctx.lineTo(centreX - size, centreY);
      ctx.closePath();
    } else ctx.rect(centreX - size, centreY - size, size * 2, size * 2);
    ctx.globalAlpha = symbol.opacity;
    ctx.fillStyle = symbol.fillColour;
    ctx.fill();
    ctx.strokeStyle = symbol.outlineColour;
    ctx.lineWidth = 1.5 * scale;
    ctx.stroke();
  } else if (symbol.kind === 'line') {
    const lineY = y + height / 2;
    const lineWidth = Math.min(6, Math.max(1.4, symbol.width * 0.72)) * scale;
    ctx.globalAlpha = symbol.opacity;
    ctx.strokeStyle = symbol.colour;
    ctx.fillStyle = symbol.colour;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.setLineDash(symbol.dash === 'dashed' ? [7 * scale, 4 * scale] : []);
    ctx.beginPath();
    ctx.moveTo(x + 2 * scale, lineY);
    ctx.lineTo(x + width - 3 * scale, lineY);
    ctx.stroke();
    const endpointX = x + width - 3 * scale;
    const endpointSize = Math.max(2.2 * scale, lineWidth);
    if (symbol.endStyle === 'filled-circle') {
      ctx.beginPath();
      ctx.arc(endpointX, lineY, endpointSize, 0, Math.PI * 2);
      ctx.fill();
    } else if (symbol.endStyle === 'filled-square') {
      ctx.fillRect(
        endpointX - endpointSize,
        lineY - endpointSize,
        endpointSize * 2,
        endpointSize * 2,
      );
    } else if (symbol.endStyle === 'arrow') {
      ctx.beginPath();
      ctx.moveTo(x + width - scale, lineY);
      ctx.lineTo(endpointX - 5 * scale, lineY - 4 * scale);
      ctx.lineTo(endpointX - 5 * scale, lineY + 4 * scale);
      ctx.closePath();
      ctx.fill();
    }
  } else if (symbol.kind === 'area') {
    const left = x + 1;
    const top = y + height * 0.15;
    const swatchWidth = width - 2;
    const swatchHeight = height * 0.7;
    ctx.beginPath();
    if (symbol.shape === 'ellipse')
      ctx.ellipse(
        left + swatchWidth / 2,
        top + swatchHeight / 2,
        swatchWidth / 2,
        swatchHeight / 2,
        0,
        0,
        Math.PI * 2,
      );
    else if (
      symbol.shape === 'rectangle' &&
      symbol.cornerRadius > 0 &&
      typeof ctx.roundRect === 'function'
    )
      ctx.roundRect(
        left,
        top,
        swatchWidth,
        swatchHeight,
        Math.min(swatchHeight / 2, symbol.cornerRadius * scale * 0.25),
      );
    else ctx.rect(left, top, swatchWidth, swatchHeight);
    ctx.globalAlpha = symbol.fillOpacity;
    ctx.fillStyle = symbol.fillColour;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = symbol.outlineColour;
    ctx.lineWidth = legendAreaOutline(symbol.outlineWidth) * scale;
    ctx.setLineDash(symbol.outlineDash === 'dashed' ? [5 * scale, 3 * scale] : []);
    if (symbol.outlineWidth > 0) ctx.stroke();
  } else {
    const outer = { x: x + 1, y: y + 1, width: width - 2, height: height - 2 };
    const inner = {
      x: x + width * 0.22,
      y: y + height * 0.28,
      width: width * 0.56,
      height: height * 0.44,
    };
    ctx.globalAlpha = symbol.fadeOpacity;
    ctx.fillStyle = symbol.fadeColour;
    if (symbol.fadeDirection === 'inside')
      ctx.fillRect(inner.x, inner.y, inner.width, inner.height);
    else {
      ctx.fillRect(outer.x, outer.y, outer.width, inner.y - outer.y);
      ctx.fillRect(
        outer.x,
        inner.y + inner.height,
        outer.width,
        outer.y + outer.height - inner.y - inner.height,
      );
      ctx.fillRect(outer.x, inner.y, inner.x - outer.x, inner.height);
      ctx.fillRect(
        inner.x + inner.width,
        inner.y,
        outer.x + outer.width - inner.x - inner.width,
        inner.height,
      );
    }
    if (symbol.boundaryVisible) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = symbol.boundaryColour;
      ctx.lineWidth = Math.min(2.5, Math.max(0.75, symbol.boundaryWidth * 0.6)) * scale;
      ctx.setLineDash(symbol.boundaryDash === 'dashed' ? [4 * scale, 2 * scale] : []);
      ctx.strokeRect(inner.x, inner.y, inner.width, inner.height);
    }
  }
  ctx.restore();
}

function applyLegendBodyTextStyle(
  ctx: CanvasRenderingContext2D,
  typography: CompositionTypography,
  dpi: number,
): void {
  ctx.globalAlpha = 1;
  ctx.fillStyle = typography.colour;
  ctx.font = typographyFont(typography, dpi);
  ctx.textAlign = canvasAlignment(typography.alignment);
  ctx.textBaseline = 'middle';
  ctx.setLineDash([]);
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  if (radius > 0 && typeof ctx.roundRect === 'function') ctx.roundRect(x, y, width, height, radius);
  else if (radius > 0) {
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + height, radius);
    ctx.arcTo(x + width, y + height, x, y + height, radius);
    ctx.arcTo(x, y + height, x, y, radius);
    ctx.arcTo(x, y, x + width, y, radius);
    ctx.closePath();
  } else ctx.rect(x, y, width, height);
}

function drawLegend(
  ctx: CanvasRenderingContext2D,
  plan: StaticCompositionRenderPlan,
  layout: LegendPhysicalPlan,
  artwork: SymbolCanvasPainter,
): void {
  if (!plan.legend.enabled) return;
  const { boundsPx: bounds } = plan.legend;
  ctx.save();
  ctx.beginPath();
  ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
  ctx.clip();
  if (plan.legend.background.enabled) {
    ctx.save();
    ctx.globalAlpha = plan.legend.background.opacity / 100;
    ctx.fillStyle = plan.legend.background.colour;
    roundedRectPath(
      ctx,
      bounds.x,
      bounds.y,
      bounds.width,
      bounds.height,
      plan.legend.cornerRadiusPx,
    );
    ctx.fill();
    ctx.restore();
  }
  if (plan.legend.border.enabled && plan.legend.borderWidthPx > 0) {
    ctx.strokeStyle = plan.legend.border.colour;
    ctx.lineWidth = plan.legend.borderWidthPx;
    const inset = plan.legend.borderWidthPx / 2;
    roundedRectPath(
      ctx,
      bounds.x + plan.legend.borderWidthPx / 2,
      bounds.y + plan.legend.borderWidthPx / 2,
      bounds.width - plan.legend.borderWidthPx,
      bounds.height - plan.legend.borderWidthPx,
      Math.max(0, plan.legend.cornerRadiusPx - inset),
    );
    ctx.stroke();
  }

  const factor = plan.pixelsPerMm;
  const drawText = (text: import('../legendLayout').LegendPlannedText) => {
    applyLegendBodyTextStyle(ctx, text.typography, plan.dpi);
    const x =
      bounds.x +
      (text.x +
        (text.typography.alignment === 'centre'
          ? text.width / 2
          : text.typography.alignment === 'right'
            ? text.width
            : 0)) *
        factor;
    ctx.textBaseline = 'top';
    text.lines.forEach((line, index) =>
      ctx.fillText(line, x, bounds.y + (text.y + index * text.lineHeight) * factor),
    );
  };
  if (layout.title) drawText(layout.title);
  for (const column of layout.columns)
    for (const fragment of column.fragments) {
      if (fragment.heading && fragment.headingState !== 'reserved') drawText(fragment.heading);
      for (const row of fragment.entries) {
        const symbol = row.symbol;
        const x = bounds.x + symbol.x * factor,
          y = bounds.y + symbol.y * factor;
        if (row.entry.annotationSymbol)
          drawAnnotationLegendSymbol(
            ctx,
            row.entry.annotationSymbol,
            x,
            y,
            symbol.width * factor,
            symbol.height * factor,
            artwork,
          );
        else if (row.entry.symbols && fragment.layer.geometry !== 'annotation')
          drawLegendSymbol(
            ctx,
            fragment.layer.geometry,
            row.entry.symbols,
            x,
            y,
            symbol.width * factor,
            symbol.height * factor,
            row.sample,
            symbol.scale * factor,
            artwork,
          );
        drawText(row.label);
      }
    }
  if (layout.empty) drawText(layout.empty);
  ctx.restore();
  if (layout.overflow)
    throw new StaticExportError(
      'legend-overflow',
      'Legend content exceeds its box. Enlarge the legend or reduce its text size before exporting.',
    );
}

function drawScaleBar(ctx: CanvasRenderingContext2D, plan: StaticCompositionRenderPlan): void {
  const bar = plan.scaleBar;
  if (!bar) return;
  const layout = bar.layout;
  const x = bar.position.x + layout.inset;
  const y = bar.position.y + layout.barY;
  const lineY = y + bar.fontSizePx * 0.48;
  const tickHeight = bar.fontSizePx * 0.62;
  const lineWidth = bar.thicknessPx;
  ctx.save();
  if (bar.panel?.background.enabled) {
    ctx.fillStyle = bar.panel.background.colour;
    ctx.globalAlpha = bar.panel.background.opacity / 100;
    roundedRectPath(
      ctx,
      bar.position.x,
      bar.position.y,
      layout.width,
      layout.height,
      layout.radius,
    );
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  if (bar.panel?.border.enabled) {
    ctx.strokeStyle = bar.panel.border.colour;
    ctx.lineWidth = layout.border;
    roundedRectPath(
      ctx,
      bar.position.x + layout.border / 2,
      bar.position.y + layout.border / 2,
      Math.max(0, layout.width - layout.border),
      Math.max(0, layout.height - layout.border),
      Math.max(0, layout.radius - layout.border / 2),
    );
    ctx.stroke();
  }
  ctx.strokeStyle = bar.colour;
  ctx.fillStyle = bar.colour;
  ctx.lineWidth = lineWidth;
  if (bar.style !== 'line') {
    const count = bar.style === 'simple-segmented' ? 2 : 4;
    for (let index = 0; index < count; index++) {
      ctx.fillStyle = index % 2 ? '#ffffff' : bar.colour;
      ctx.fillRect(x + (index * bar.widthPx) / count, y, bar.widthPx / count, layout.barHeight);
    }
    ctx.strokeRect(
      x + lineWidth / 2,
      y + lineWidth / 2,
      bar.widthPx - lineWidth,
      layout.barHeight - lineWidth,
    );
  } else {
    ctx.beginPath();
    ctx.moveTo(x, lineY);
    ctx.lineTo(x + bar.widthPx, lineY);
    for (const fraction of [0, 0.5, 1]) {
      const tickX = x + bar.widthPx * fraction;
      ctx.moveTo(tickX, lineY - tickHeight);
      ctx.lineTo(tickX, lineY);
    }
    ctx.stroke();
  }
  ctx.fillStyle = bar.colour;
  ctx.font = `normal 400 ${bar.fontSizePx}px "PlaceioMaps-Inter"`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(
    '0',
    x,
    bar.style !== 'line' ? bar.position.y + layout.labelY : lineY + bar.fontSizePx * 0.28,
  );
  if (bar.style === 'simple-segmented') {
    ctx.textAlign = 'center';
    ctx.fillText(bar.midpointLabel, x + bar.widthPx / 2, bar.position.y + layout.labelY);
  }
  ctx.font = `normal ${bar.style === 'simple-segmented' ? 400 : 700} ${bar.fontSizePx}px "PlaceioMaps-Inter"`;
  ctx.textAlign = 'right';
  ctx.fillText(
    bar.label,
    x + bar.widthPx,
    bar.style !== 'line' ? bar.position.y + layout.labelY : lineY + bar.fontSizePx * 0.28,
  );
  ctx.restore();
}

function drawNorthArrow(ctx: CanvasRenderingContext2D, plan: StaticCompositionRenderPlan): void {
  const arrow = plan.northArrow;
  if (!arrow) return;
  const scale = arrow.sizePx / 100;
  ctx.save();
  ctx.translate(arrow.position.x + arrow.sizePx / 2, arrow.position.y + arrow.sizePx / 2);
  ctx.rotate((arrow.rotationDegrees * Math.PI) / 180);
  ctx.translate(-50 * scale, -50 * scale);
  ctx.scale(scale, scale);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  for (const primitive of northArrowPrimitives(arrow.style)) {
    ctx.beginPath();
    if (primitive.kind === 'circle') {
      ctx.arc(primitive.centre[0], primitive.centre[1], primitive.radius, 0, Math.PI * 2);
      for (const cutout of primitive.cutouts ?? []) {
        cutout.points.forEach(([x, y], index) =>
          index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y),
        );
        ctx.closePath();
      }
    } else {
      primitive.points.forEach(([x, y], index) =>
        index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y),
      );
      if (primitive.closed) ctx.closePath();
    }
    if (primitive.fill) {
      ctx.fillStyle = primitive.fill === 'white' ? '#ffffff' : arrow.colour;
      ctx.fill('evenodd');
    }
    if (primitive.stroke && primitive.strokeWidth > 0) {
      ctx.strokeStyle = arrow.colour;
      ctx.lineWidth = primitive.strokeWidth;
      ctx.lineCap = primitive.kind === 'path' ? (primitive.lineCap ?? 'round') : 'round';
      ctx.stroke();
    }
  }
  if (arrow.showLabel) {
    ctx.fillStyle = arrow.colour;
    ctx.font = 'normal 700 23px "PlaceioMaps-Inter"';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('N', 50, 21);
  }
  ctx.restore();
}

export type CompositionLogoImageLoader = (dataUrl: string) => Promise<CanvasImageSource>;

export const loadCompositionLogoImage: CompositionLogoImageLoader = async (dataUrl) => {
  const image = new Image();
  image.src = dataUrl;
  try {
    await image.decode();
  } catch (cause) {
    throw new StaticExportError('image', 'Could not decode the logo for export.', { cause });
  }
  return image;
};

export interface CompositionPageRendererDependencies {
  readonly symbolImages?: PreparedSymbolCatalogue;
  readonly legendMeasurement?: LegendTextMeasurement;
  readonly insetCapture?: import('./insetRenderer').InsetCapture;
  readonly loadLogoImage?: CompositionLogoImageLoader;
}

export async function renderCompositionPage(
  plan: StaticCompositionRenderPlan,
  mapCanvas: HTMLCanvasElement | ImageBitmap,
  dependencies: CompositionPageRendererDependencies = {},
): Promise<HTMLCanvasElement> {
  const artwork = new SymbolCanvasPainter(dependencies.symbolImages);
  await ensureCompositionFontsLoaded(plan);
  const measurement =
    dependencies.legendMeasurement ??
    (plan.legend.enabled ? await loadLegendMeasurement(plan.legend) : undefined);
  const legendLayout = measurement ? staticLegendLayout(plan, measurement) : null;
  if (legendLayout?.overflow)
    throw new StaticExportError(
      'legend-overflow',
      'Legend content exceeds its box. Enlarge the legend or reduce its text size before exporting.',
    );
  const logoImage = plan.logo
    ? await (dependencies.loadLogoImage ?? loadCompositionLogoImage)(plan.logo.dataUrl)
    : null;
  // Snapshotting first lets the browser schedule the WebGL readback asynchronously. Drawing the
  // live WebGL canvas directly caused the measured multi-second high-DPI main-thread block.
  const mapImage =
    typeof createImageBitmap === 'function' && mapCanvas instanceof HTMLCanvasElement
      ? await createImageBitmap(mapCanvas)
      : mapCanvas;
  const ownsBitmap = mapImage !== mapCanvas;
  const canvas = document.createElement('canvas');
  canvas.width = plan.page.widthPx;
  canvas.height = plan.page.heightPx;
  try {
    // This full-resolution surface is read back for PNG/PDF, never presented onscreen.
    // Avoid an accelerated page canvas and its additional GPU readback during encoding.
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new StaticExportError('canvas', 'Could not create the export page.');

    ctx.fillStyle = plan.page.backgroundColour;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const frame = plan.mapFrame.boundsPx;
    ctx.save();
    roundedRectPath(ctx, frame.x, frame.y, frame.width, frame.height, plan.mapFrame.cornerRadiusPx);
    ctx.clip();
    try {
      ctx.drawImage(mapImage, frame.x, frame.y, frame.width, frame.height);
    } catch (cause) {
      throw new StaticExportError(
        'cors',
        'This basemap could not be included in the static export.',
        {
          cause,
        },
      );
    } finally {
      ctx.restore();
    }
    if (plan.mapFrame.borderEnabled && plan.mapFrame.borderWidthPx > 0) {
      ctx.strokeStyle = plan.mapFrame.borderColour;
      ctx.lineWidth = plan.mapFrame.borderWidthPx;
      roundedRectPath(
        ctx,
        frame.x + plan.mapFrame.borderWidthPx / 2,
        frame.y + plan.mapFrame.borderWidthPx / 2,
        frame.width - plan.mapFrame.borderWidthPx,
        frame.height - plan.mapFrame.borderWidthPx,
        Math.max(0, plan.mapFrame.cornerRadiusPx - plan.mapFrame.borderWidthPx / 2),
      );
      ctx.stroke();
    }
    if (plan.insetMap) {
      const capture = dependencies.insetCapture;
      if (!capture)
        throw new StaticExportError('image', 'The inset map was not rendered for export.');
      const { boundsPx: bounds, config } = plan.insetMap;
      const radius = Math.min(
        config.cornerRadiusMm * plan.pixelsPerMm,
        bounds.width / 2,
        bounds.height / 2,
      );
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(bounds.x, bounds.y, bounds.width, bounds.height, radius);
      ctx.clip();
      ctx.drawImage(capture.canvas, bounds.x, bounds.y, bounds.width, bounds.height);
      ctx.beginPath();
      capture.points.forEach((p, i) => {
        const x = bounds.x + p.x * bounds.width,
          y = bounds.y + p.y * bounds.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = config.indicator.fillColour;
      ctx.globalAlpha = config.indicator.fillOpacity / 100;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = config.indicator.outlineColour;
      ctx.lineWidth = config.indicator.outlineWidthMm * plan.pixelsPerMm;
      ctx.lineJoin = 'round';
      ctx.stroke();
      if (config.border.enabled) {
        const line = config.border.widthMm * plan.pixelsPerMm;
        ctx.beginPath();
        ctx.roundRect(
          bounds.x + line / 2,
          bounds.y + line / 2,
          Math.max(0, bounds.width - line),
          Math.max(0, bounds.height - line),
          Math.max(0, radius - line / 2),
        );
        ctx.strokeStyle = config.border.colour;
        ctx.lineWidth = line;
        ctx.stroke();
      }
      ctx.restore();
    }
    drawText(ctx, plan.title, plan.dpi);
    drawText(ctx, plan.subtitle, plan.dpi);
    drawText(ctx, plan.sourcesAttribution, plan.dpi, true);
    if (legendLayout) drawLegend(ctx, plan, legendLayout, artwork);
    drawScaleBar(ctx, plan);
    drawNorthArrow(ctx, plan);
    if (plan.logo && logoImage) {
      const bounds = plan.logo.boundsPx;
      try {
        ctx.drawImage(logoImage, bounds.x, bounds.y, bounds.width, bounds.height);
      } catch (cause) {
        throw new StaticExportError('image', 'Could not draw the logo in this export.', { cause });
      }
    }
    return canvas;
  } catch (cause) {
    canvas.width = 1;
    canvas.height = 1;
    throw cause;
  } finally {
    artwork.clear();
    if (ownsBitmap && typeof ImageBitmap !== 'undefined' && mapImage instanceof ImageBitmap)
      mapImage.close();
  }
}
